import React from 'react';
import styled from 'styled-components';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import { IinventoryItem } from '@typings/store/storeItem.interface';
import { IPhotoCard } from '@typings/photocard/photocard.interface';
import InventoryItem from './InventoryItem';
import InventoryDetailModal from './InventoryDetailModal';
import { CollectionPath } from '@typings/collections.enum';

// ─── Styled ──────────────────────────────────────────────────────────────────

/** 모든 슬롯 공통 컨테이너 — 게임 인벤토리 스타일 */
const GridSlot = styled.div<{ $isOver?: boolean; $isDraggable?: boolean; $isEmpty?: boolean }>`
  position: relative;
  width: 100%;
  min-width: 50px;
  min-height: 50px;
  aspect-ratio: 1 / 1;
  border-radius: 2px;
  box-sizing: border-box;

  /* 빈 슬롯: 도트 패턴 */
  background: ${({ $isEmpty }) =>
    $isEmpty
      ? `radial-gradient(circle, var(--lineColor) 0.8px, transparent 0.8px)`
      : 'var(--subBgColor, rgba(0,0,0,0.02))'};
  background-size: ${({ $isEmpty }) => ($isEmpty ? '6px 6px' : 'auto')};

  /* 드롭 타겟 하이라이트 */
  box-shadow: ${({ $isOver }) =>
    $isOver
      ? 'inset 0 0 0 2px var(--primaryColor), inset 0 0 8px rgba(99,102,241,0.25)'
      : 'inset 0 1px 2px rgba(0,0,0,0.1)'};

  /* 슬롯 사이 경계선 대신 outline 사용 (gap 0 상태에서 겹치지 않도록) */
  outline: 1px solid
    ${({ $isOver }) => ($isOver ? 'var(--primaryColor)' : 'var(--lineColor)')};
  outline-offset: -1px;
  border: none;

  transition: box-shadow 0.15s, outline-color 0.15s, background 0.15s;
  cursor: ${({ $isDraggable, $isEmpty }) =>
    $isDraggable && $isEmpty
      ? 'pointer'
      : $isDraggable
      ? 'var(--hoverImgURL), pointer'
      : 'var(--defaultImgURL), auto'};

  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--subTextColor);
  font-size: 13px;
  overflow: hidden;

  /* hover 인터랙션: 라인 primaryColor + 블랙 오버레이 */
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.2);
    border-radius: inherit;
    pointer-events: none;
    opacity: 0;
    transition: opacity 0.15s;
    z-index: 1;
  }

  &:hover {
    outline-color: var(--primaryColor);

    &::after {
      opacity: 1;
    }
  }

  /* 드래그 모드: 텍스트 선택 / 이미지 드래그 방지 */
  user-select: none;
  -webkit-user-select: none;
  -webkit-touch-callout: none;

  /* 아이템 이미지가 슬롯을 꽉 채우도록 */
  & img {
    -webkit-user-drag: none;
    pointer-events: none;
  }
`;

/** 드래그 래퍼 — 드래그 중이면 원본 숨김 (DragOverlay가 대신 표시) */
const DraggableWrapper = styled.div<{ $isDragging?: boolean }>`
  width: 100%;
  height: 100%;
  position: relative;
  opacity: ${({ $isDragging }) => ($isDragging ? 0.3 : 1)};
  transition: opacity 0.15s ease;
  cursor: var(--hoverImgURL), pointer;
  &:active {
    cursor: var(--hoverImgURL), pointer;
  }
  touch-action: none;
`;

// ─── Props ───────────────────────────────────────────────────────────────────

type Props = {
  slotIndex: number;
  item?: IinventoryItem;
  isDraggable?: boolean;
  onEmptySlotClick?: (slotIndex: number) => void;
  /** 우클릭(PC) / 더블탭(모바일)으로 장착 해제 */
  onUnequip?: (item: IinventoryItem) => void;
  /** 슬롯 hover 시 호출 */
  onSlotHover?: () => void;
  photocardData?: IPhotoCard;
  hidden?: boolean;
};

// ─── Component ───────────────────────────────────────────────────────────────

function InventorySlot({
  slotIndex,
  item,
  isDraggable,
  onEmptySlotClick,
  onUnequip,
  onSlotHover,
  photocardData,
  hidden,
}: Props) {
  // 모바일 더블탭 감지용
  const lastTapRef = React.useRef<number>(0);
  // 모든 슬롯은 드롭 타겟
  const dropId = `slot-${slotIndex}`;
  const { setNodeRef: setDropRef, isOver } = useDroppable({ id: dropId });

  // 아이템이 있는 슬롯만 드래그 소스 (편집모드 시)
  const draggableId = item ? item.id : '';
  const {
    attributes,
    listeners,
    setNodeRef: setDragRef,
    transform,
    isDragging,
  } = useDraggable({
    id: draggableId || `empty-drag-${slotIndex}`,
    disabled: !item || !isDraggable,
  });

  // transform은 DragOverlay가 담당하므로 원본에는 적용하지 않음
  const dragStyle: React.CSSProperties = {};

  // PC: 우클릭으로 장착 해제
  const handleContextMenu = React.useCallback(
    (e: React.MouseEvent) => {
      if (!isDraggable || !item || !onUnequip) return;
      e.preventDefault();
      onUnequip(item);
    },
    [isDraggable, item, onUnequip],
  );

  // 모바일: 더블탭으로 장착 해제
  const handleTouchEnd = React.useCallback(
    (e: React.TouchEvent) => {
      if (!isDraggable || !item || !onUnequip) return;
      const now = Date.now();
      if (now - lastTapRef.current < 300) {
        e.preventDefault();
        onUnequip(item);
        lastTapRef.current = 0;
      } else {
        lastTapRef.current = now;
      }
    },
    [isDraggable, item, onUnequip],
  );

  if (!item) {
    return (
      <GridSlot
        ref={setDropRef}
        $isOver={isOver}
        $isDraggable={isDraggable}
        $isEmpty
        onMouseEnter={onSlotHover}
        onClick={() => isDraggable && onEmptySlotClick?.(slotIndex)}
      >
        {isDraggable && '+'}
      </GridSlot>
    );
  }

  const hiddenStyle: React.CSSProperties = hidden
    ? { opacity: 0.15, pointerEvents: 'none', transition: 'opacity 0.2s' }
    : { transition: 'opacity 0.2s' };

  return (
    <GridSlot
      ref={setDropRef}
      $isOver={isOver}
      $isDraggable={isDraggable}
      $isEmpty={false}
      style={hiddenStyle}
      onMouseEnter={onSlotHover}
      onContextMenu={handleContextMenu}
      onTouchEnd={handleTouchEnd}
    >
      <DraggableWrapper
        ref={setDragRef}
        style={dragStyle}
        $isDragging={isDragging}
        {...(isDraggable ? listeners : {})}
        {...(isDraggable ? attributes : {})}
      >
        <InventoryItem
          data={item}
          photocardData={photocardData}
          modal={<InventoryDetailModal />}
          collectionPath={CollectionPath.STORE}
          followTooltip={false}
        />
      </DraggableWrapper>
    </GridSlot>
  );
}

export default InventorySlot;
